import { getUrl } from '../../config';
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { 
    Tooltip, 
    Menu, 
    MenuItem, 
    IconButton as MuiIconButton, 
    ListItemIcon, 
    LinearProgress, 
    Typography, 
    Box, 
    Checkbox, 
    Button, 
    Divider, 
    FormControlLabel,
    Avatar
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import styled from "@emotion/styled";

const IconButton = styled(MuiIconButton)`
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease-in-out;
  }
  border-radius: 8px;
  padding: 8px;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: ${(props) =>
      props.theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(0, 0, 0, 0.04)"};
    color: ${(props) => props.theme.palette.primary.main};
    svg {
      transform: scale(1.05);
    }
  }
`;

const getErpColor = (erpId, themeMode) => {
    const colors = [
        { light: '#E0F2FE', dark: 'rgba(56, 189, 248, 0.15)', textLight: '#0284c7', textDark: '#38BDF8' }, // Light Blue
        { light: '#DCFCE7', dark: 'rgba(74, 222, 128, 0.15)', textLight: '#16a34a', textDark: '#4ADE80' }, // Emerald
        { light: '#F3E8FF', dark: 'rgba(192, 132, 252, 0.15)', textLight: '#9333ea', textDark: '#C084FC' }, // Purple
        { light: '#FEF3C7', dark: 'rgba(251, 191, 36, 0.15)', textLight: '#d97706', textDark: '#FBBF24' }, // Amber
        { light: '#FFE4E6', dark: 'rgba(251, 113, 133, 0.15)', textLight: '#e11d48', textDark: '#FB7185' }, // Rose
        { light: '#E0F2F1', dark: 'rgba(45, 212, 191, 0.15)', textLight: '#0d9488', textDark: '#2DD4BF' }, // Teal
    ];
    const index = (erpId || 0) % colors.length;
    const color = colors[index];
    return themeMode === 'dark' ? { bg: color.dark, text: color.textDark } : { bg: color.light, text: color.textLight };
};

function NavbarErp() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [loadingErp, setLoadingErp] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState('');
    const [erps, setErps] = useState([]);
    const [currentErpId, setCurrentErpId] = useState(null);
    const [currentErpName, setCurrentErpName] = useState('ERP');
    const [isEditMode, setIsEditMode] = useState(false);
    const [erpPreferences, setErpPreferences] = useState({
        order: [],
        visible: {}
    });

    // Charger les préférences utilisateur depuis localStorage
    const loadUserPreferences = (erpsList) => {
        const userId = window.localStorage.getItem("userId");
        const savedPrefs = window.localStorage.getItem(`erpPreferences_${userId}`);
        
        if (savedPrefs) {
            const prefs = JSON.parse(savedPrefs);
            setErpPreferences(prefs);
            
            // Réorganiser la liste selon l'ordre sauvegardé
            if (prefs.order && prefs.order.length > 0) {
                const orderedErps = [];
                prefs.order.forEach(erpId => {
                    const erp = erpsList.find(e => e.erpId === erpId);
                    if (erp) orderedErps.push(erp);
                });
                
                // Ajouter les nouveaux ERPs qui ne sont pas dans l'ordre sauvegardé
                erpsList.forEach(erp => {
                    if (!prefs.order.includes(erp.erpId)) {
                        orderedErps.push(erp);
                    }
                });
                
                return orderedErps;
            }
        } else {
            // Initialiser les préférences si aucune n'existe
            const initialPrefs = {
                order: erpsList.map(erp => erp.erpId),
                visible: erpsList.reduce((acc, erp) => {
                    acc[erp.erpId] = true;
                    return acc;
                }, {})
            };
            setErpPreferences(initialPrefs);
        }
        
        return erpsList;
    };

    const fetchErps = async () => {
        try {
            const response = await axios.get(getUrl() + "/api/erps");
            if (response.data.status === 'success') {
                const orderedErps = loadUserPreferences(response.data.data);
                setErps(orderedErps);
                console.log("erps", orderedErps);
                
                // Mettre à jour le nom dans le state/localStorage si disponible
                const savedErpId = window.localStorage.getItem("erpId");
                if (savedErpId) {
                    const match = orderedErps.find(e => e.erpId === parseInt(savedErpId));
                    if (match) {
                        setCurrentErpName(match.name);
                        window.localStorage.setItem("erpName", match.name);
                    }
                }
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des ERPs:", error);
        }
    };
    const toggleMenu = (event) => {
        setAnchorMenu(event.currentTarget);
        fetchErps();
    };

    const closeMenu = () => {
        setAnchorMenu(null);
        setIsEditMode(false);
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(erps);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setErps(items);
        setErpPreferences(prev => ({
            ...prev,
            order: items.map(erp => erp.erpId)
        }));
    };

    const handleVisibilityToggle = (erpId) => {
        setErpPreferences(prev => ({
            ...prev,
            visible: {
                ...prev.visible,
                [erpId]: prev.visible[erpId] === false ? true : false
            }
        }));
    };

    const savePreferences = () => {
        const userId = window.localStorage.getItem("userId");
        window.localStorage.setItem(`erpPreferences_${userId}`, JSON.stringify(erpPreferences));
        setIsEditMode(false);
    };

    const handleErpChange = async (erp) => {
        closeMenu();
        const erpName = erp.name;

        // Créer les styles CSS pour les animations du loader
        const style = document.createElement('style');
        style.id = 'erp-loader-styles';
        style.innerHTML = `
            @keyframes pulseGlow {
                0%, 100% { opacity: 0.8; transform: scale(0.99); }
                50% { opacity: 1; transform: scale(1.01); }
            }
            @keyframes spinLoader {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            .loading-container {
                animation: pulseGlow 2.5s ease-in-out infinite;
                text-align: center;
                display: flex;
                flex-direction: column;
                align-items: center;
            }
            .spinner-ring {
                width: 48px;
                height: 48px;
                border: 3.5px solid rgba(255, 255, 255, 0.05);
                border-top: 3.5px solid #3b82f6;
                border-radius: 50%;
                animation: spinLoader 0.8s linear infinite;
                margin-bottom: 24px;
            }
        `;
        document.head.appendChild(style);

        // Créer dynamiquement un overlay HTML au-dessus de tout
        const overlay = document.createElement('div');
        overlay.id = 'erp-loader-overlay';
        overlay.style.position = 'fixed';
        overlay.style.top = 0;
        overlay.style.left = 0;
        overlay.style.width = '100vw';
        overlay.style.height = '100vh';
        overlay.style.background = 'radial-gradient(circle at center, #1e293b 0%, #0f172a 100%)';
        overlay.style.zIndex = 99999;
        overlay.style.display = 'flex';
        overlay.style.flexDirection = 'column';
        overlay.style.justifyContent = 'center';
        overlay.style.alignItems = 'center';
        overlay.style.fontFamily = 'Inter, system-ui, -apple-system, sans-serif';

        // Ajoute le contenu
        overlay.innerHTML = `
        <div class="loading-container" style="width: 320px; padding: 40px; border-radius: 16px; background: rgba(255, 255, 255, 0.02); backdrop-filter: blur(16px); border: 1px solid rgba(255, 255, 255, 0.05); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);">
            <div class="spinner-ring"></div>
            <h1 style="font-size: 20px; font-weight: 700; margin: 0 0 6px 0; color: #f8fafc; letter-spacing: -0.01em;">Chargement de l'ERP</h1>
            <p style="font-size: 15px; color: #94a3b8; margin: 0 0 24px 0; font-weight: 500;">${erpName}</p>
            <div id="progress-bar" style="width: 100%; height: 6px; background-color: rgba(255, 255, 255, 0.08); border-radius: 9999px; overflow: hidden; margin-bottom: 16px;">
                <div id="progress-fill" style="
                    width: 0%;
                    height: 100%;
                    background: linear-gradient(90deg, #3b82f6, #6366f1);
                    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border-radius: 9999px;
                "></div> 
            </div>
            <p id="progress-text" style="font-size: 12px; color: #3b82f6; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin: 0;">Initialisation...</p>
        </div>
        `;
        document.body.appendChild(overlay);

        // Animation de la barre de progression
        let progress = 0;
        const messages = [
            'Récupération des informations...',
            'Chargement des configurations...',
            'Affichage des données...',
            'Finalisation...'
        ];

        const interval = setInterval(() => {
            progress += Math.random() * 30;
            if (progress >= 100) {
                clearInterval(interval);
                navigate(`/`);
                window.location.reload();
            } else {
                const fill = document.getElementById('progress-fill');
                const text = document.getElementById('progress-text');
                if (fill) fill.style.width = `${Math.min(progress, 100)}%`;
                if (text) {
                    if (progress < 25) text.innerText = messages[0];
                    else if (progress < 50) text.innerText = messages[1];
                    else if (progress < 75) text.innerText = messages[2];
                    else text.innerText = messages[3];
                }
            }
        }, 300);

        try {
            const response = await axios.post(`${getUrl()}/api/setUserErpId`, { erpId: erp.erpId });
            window.localStorage.setItem("erpId", erp.erpId);
            window.localStorage.setItem("erpName", erp.name);
            setCurrentErpId(erp.erpId);
            setCurrentErpName(erp.name);
            
            // Déclencher l'événement pour mettre à jour le logo
            window.dispatchEvent(new CustomEvent('erpChanged'));
        } catch (error) {
            console.error("Erreur lors du changement d'erp :", error);
        }
    };

    // Gestion de la fausse progression (fallback)
    useEffect(() => {
        if (loadingErp) {
            const interval = setInterval(() => {
                setProgress((oldProgress) => {
                    const newProgress = oldProgress + Math.random() * 20;
                    if (newProgress >= 100) {
                        clearInterval(interval);
                        setProgress(100);
                        setTimeout(() => {
                            window.location.reload();
                        }, 500);
                    }
                    return Math.min(newProgress, 100);
                });
            }, 800);
        }
    }, [loadingErp]);

    // Mise à jour du texte selon la progression
    useEffect(() => {
        if (progress < 25) {
            setCurrentStep('Récupération des informations...');
        } else if (progress < 50) {
            setCurrentStep('Chargement des informations...');
        } else if (progress < 75) {
            setCurrentStep('Affichage des informations...');
        } else if (progress < 100) {
            setCurrentStep('Actualisation...');
        }
    }, [progress]);

    // Initialiser le currentErpId au montage du composant et charger les ERPs
    useEffect(() => {
        const savedErpId = window.localStorage.getItem("erpId");
        const savedErpName = window.localStorage.getItem("erpName");
        if (savedErpId) {
            setCurrentErpId(parseInt(savedErpId));
        }
        if (savedErpName) {
            setCurrentErpName(savedErpName);
        }
        fetchErps();
    }, []);

    // Filtrer les ERPs visibles
    const visibleErps = erps.filter(erp => 
        isEditMode || erpPreferences.visible[erp.erpId] !== false
    );

    const activeErp = erps.find(e => e.erpId === currentErpId);

    return (
        <React.Fragment>
            <Tooltip title="Changer d'ERP">
                <Button
                    aria-owns={Boolean(anchorMenu) ? "menu-appbar" : undefined}
                    aria-haspopup="true"
                    onClick={toggleMenu}
                    color="inherit"
                    startIcon={<BusinessIcon sx={{ color: 'primary.main', fontSize: '20px !important' }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.825rem',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                        }
                    }}
                >
                    {activeErp ? activeErp.name : currentErpName}
                </Button>
            </Tooltip>
            <Menu
                id="menu-appbar"
                anchorEl={anchorMenu}
                open={Boolean(anchorMenu)}
                onClose={closeMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        minWidth: isEditMode ? '380px' : '280px',
                        borderRadius: '12px',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        boxShadow: (theme) => 
                            theme.palette.mode === 'dark' 
                                ? '0 10px 30px rgba(0, 0, 0, 0.5)' 
                                : '0 10px 30px rgba(0, 0, 0, 0.08)',
                        background: (theme) => 
                            theme.palette.mode === 'dark'
                                ? 'rgba(30, 41, 59, 0.95)'
                                : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(12px)',
                        padding: '4px 0',
                        overflow: 'hidden'
                    }
                }}
            >
                {/* Header avec boutons de réorganisation */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.02em', flexShrink: 0 }}>
                        {isEditMode ? 'Configuration' : 'Sélectionner un ERP'}
                    </Typography>
                    {!isEditMode ? (
                        <Tooltip title="Gérer l'affichage">
                            <IconButton size="small" onClick={toggleEditMode} color="primary" sx={{ borderRadius: '6px' }}>
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <Button 
                                size="small" 
                                variant="contained" 
                                color="success" 
                                onClick={savePreferences} 
                                startIcon={<SaveIcon fontSize="small" />}
                                sx={{ 
                                    borderRadius: '6px', 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    px: 1.2,
                                    py: 0.4,
                                    boxShadow: 'none',
                                    minWidth: 0,
                                    '&:hover': { boxShadow: 'none' }
                                }}
                            >
                                Sauver
                            </Button>
                            <Button 
                                size="small" 
                                variant="outlined" 
                                color="error" 
                                onClick={() => setIsEditMode(false)} 
                                startIcon={<CloseIcon fontSize="small" />}
                                sx={{ 
                                    borderRadius: '6px', 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    px: 1.2,
                                    py: 0.4,
                                    minWidth: 0
                                }}
                            >
                                Annuler
                            </Button>
                        </Box>
                    )}
                </Box>
                <Divider />

                {/* Conteneur défilant pour la liste des ERPs */}
                <Box sx={{ 
                    maxHeight: '340px', 
                    overflowY: 'auto',
                    py: 1,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.24)' : 'rgba(0, 0, 0, 0.24)',
                    }
                }}>
                    {/* Mode édition avec drag & drop */}
                    {isEditMode ? (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="erps-list">
                                {(provided) => (
                                    <Box
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        sx={{ px: 1 }}
                                    >
                                        {erps.map((erp, index) => {
                                            const colors = getErpColor(erp.erpId, theme.palette.mode);
                                            return (
                                                <Draggable
                                                    key={erp.erpId}
                                                    draggableId={String(erp.erpId)}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                px: 1.5,
                                                                py: 1,
                                                                backgroundColor: snapshot.isDragging 
                                                                    ? (theme) => theme.palette.mode === 'dark' ? 'rgba(71, 130, 218, 0.15)' : 'rgba(71, 130, 218, 0.08)'
                                                                    : (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                                                borderRadius: '8px',
                                                                mx: 0.5,
                                                                mb: 1,
                                                                border: snapshot.isDragging 
                                                                    ? (theme) => `1.5px solid ${theme.palette.primary.main}`
                                                                    : (theme) => `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                                                boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
                                                                transition: 'background-color 0.2s, border-color 0.2s',
                                                            }}
                                                        >
                                                            <Box 
                                                                {...provided.dragHandleProps} 
                                                                sx={{ 
                                                                    display: 'flex', 
                                                                    mr: 1.5, 
                                                                    color: 'text.secondary', 
                                                                    cursor: 'grab',
                                                                    '&:active': { cursor: 'grabbing' },
                                                                    '&:hover': { color: 'primary.main' }
                                                                }}
                                                            >
                                                                <DragIndicatorIcon fontSize="small" />
                                                            </Box>
                                                            
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                                                <Avatar sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    borderRadius: '6px',
                                                                    backgroundColor: colors.bg,
                                                                    color: colors.text,
                                                                }}>
                                                                    {erp.name ? erp.name.substring(0, 2).toUpperCase() : 'EP'}
                                                                </Avatar>
                                                                <Typography 
                                                                    variant="body2" 
                                                                    sx={{ 
                                                                        fontWeight: 600,
                                                                        color: erpPreferences.visible[erp.erpId] !== false ? 'text.primary' : 'text.disabled',
                                                                        textDecoration: erpPreferences.visible[erp.erpId] !== false ? 'none' : 'line-through',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {erp.name}
                                                                </Typography>
                                                            </Box>

                                                            <Tooltip title={erpPreferences.visible[erp.erpId] !== false ? "Masquer" : "Afficher"}>
                                                                <MuiIconButton 
                                                                    size="small" 
                                                                    onClick={() => handleVisibilityToggle(erp.erpId)} 
                                                                    sx={{ 
                                                                        color: erpPreferences.visible[erp.erpId] !== false ? 'success.main' : 'text.disabled',
                                                                        backgroundColor: erpPreferences.visible[erp.erpId] !== false ? (theme) => theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(74, 222, 128, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                                                        '&:hover': {
                                                                            backgroundColor: erpPreferences.visible[erp.erpId] !== false ? (theme) => theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(74, 222, 128, 0.16)' : 'rgba(0, 0, 0, 0.08)',
                                                                        },
                                                                        borderRadius: '6px',
                                                                        p: 0.75
                                                                    }}
                                                                >
                                                                    {erpPreferences.visible[erp.erpId] !== false ? (
                                                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                                                    ) : (
                                                                        <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                                                    )}
                                                                </MuiIconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        /* Mode normal - liste des ERPs */
                        visibleErps.map((erp) => {
                            const isSelected = currentErpId === erp.erpId;
                            const colors = getErpColor(erp.erpId, theme.palette.mode);
                            return (
                                <MenuItem 
                                    key={erp.erpId} 
                                    onClick={() => handleErpChange(erp)}
                                    selected={isSelected}
                                    sx={{
                                        mx: 1,
                                        my: 0.5,
                                        py: 1.2,
                                        px: 1.5,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.2s ease-in-out',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: (theme) => 
                                                theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.04)' 
                                                    : 'rgba(0, 0, 0, 0.02)',
                                            borderColor: (theme) => 
                                                theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.06)' 
                                                    : 'rgba(0, 0, 0, 0.04)',
                                            transform: 'translateX(3px)'
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: (theme) => 
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(59, 130, 246, 0.12)'
                                                    : 'rgba(59, 130, 246, 0.06)',
                                            borderColor: (theme) => 
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(59, 130, 246, 0.25)'
                                                    : 'rgba(59, 130, 246, 0.15)',
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: (theme) => 
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(59, 130, 246, 0.18)'
                                                        : 'rgba(59, 130, 246, 0.18)',
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                        <Avatar sx={{
                                            width: 32,
                                            height: 32,
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            borderRadius: '8px',
                                            backgroundColor: colors.bg,
                                            color: colors.text,
                                            transition: 'all 0.2s ease',
                                        }}>
                                            {erp.name ? erp.name.substring(0, 2).toUpperCase() : 'EP'}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: isSelected ? 700 : 600,
                                                    color: isSelected ? 'primary.main' : 'text.primary',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {erp.name}
                                            </Typography>
                                            {isSelected && (
                                                <Typography variant="caption" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'success.main' }} />
                                                    Actif
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    {isSelected && (
                                        <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1, flexShrink: 0 }} />
                                    )}
                                </MenuItem>
                            );
                        })
                    )}
                </Box>
            </Menu>
        </React.Fragment>
    );
}

export default NavbarErp;